/** Identifiants des 8 jeux, dans l'ordre du hub. */
export type GameId =
  'reseau' | 'miroirs' | 'vitrail' | 'duo' | 'lumieres' | 'rotor' | 'filaments' | 'ponts';

export const GAME_IDS: readonly GameId[] = [
  'reseau',
  'miroirs',
  'vitrail',
  'duo',
  'lumieres',
  'rotor',
  'filaments',
  'ponts',
] as const;

/** Niveau de difficulté (profondeur logique). */
export type Diff = 0 | 1 | 2;

export const DIFFS = ['Facile', 'Moyen', 'Difficile'] as const;
export const DIFF_IC = ['🌱', '🔥', '💎'] as const;
export const SIZE_NAMES = ['Éclair', 'Classique', 'Épopée', 'Immense'] as const;

export const NB_DEFIS = 6;
export const NB_SIZES = 4;
export const NB_DIFFS = 3;

/** Résultat du barème : points plein tarif, étoiles 1-3 et part « bonus étoiles » des points. */
export interface ScoreResult {
  pts: number;
  stars: 1 | 2 | 3;
  bonus: number;
}

/** Bonus d'étoiles : 0★→0, 2★→40×(diff+1), 3★→100×(diff+1). Exporté pour la politique de rejeu. */
export function starBonus(stars: number, diff: number): number {
  return [0, 0, 40, 100][Math.max(0, Math.min(3, stars))] * (diff + 1);
}

/**
 * Barème de performance (remplace le forfait v9 « (diff+1)×100×(sizeIdx+1) »).
 * - base 100..900 selon niveau × taille ;
 * - efficacité coups (par/coups) et temps (budget 4/6/8 s par coup selon la taille) ;
 * - une victoire brouillonne ne rapporte que ~45 % de la base ;
 * - étoiles : 3★ = coups ≤ ~1,18×par ET temps dans le budget ; 2★ = coups ≤ ~1,67×par.
 * La politique premier essai / rejeu (×0,25 hors amélioration d'étoiles) est appliquée par l'UI.
 */
export function scoreFor(
  diff: number,
  sizeIdx: number,
  par: number,
  coups: number,
  tempsMs: number,
): ScoreResult {
  const p = Math.max(1, par);
  const base = 100 * (diff + 1) * (sizeIdx + 1);
  const effM = Math.min(1, p / Math.max(coups, 1));
  const tPar = p * (4 + 2 * sizeIdx) * 1000;
  const effT = Math.min(1, tPar / Math.max(tempsMs, 3000));
  const perf = 0.45 + 0.35 * effM + 0.2 * effT;
  const stars: 1 | 2 | 3 = effM >= 0.85 && effT >= 0.55 ? 3 : effM >= 0.6 ? 2 : 1;
  const bonus = starBonus(stars, diff);
  return { pts: Math.round(base * perf) + bonus, stars, bonus };
}

/** Codes de salle : alphabet 31 signes sans ambigus (index.html:2076). */
export const ROOM_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
/**
 * Longueur des codes : 4 signes → 31⁴ = 923 521 combinaisons (docs/03 §2.5, §6.3).
 * Non séquentiels + rate-limit join ⇒ énumération impraticable. Court = lisible/épelable
 * par des enfants (arbitrage docs/03 vs « 5 signes » du plan docs/06 — voir rapport Phase 3).
 */
export const ROOM_CODE_LEN = 4;
export const ROOM_PREFIX = 'ordrecache8-';

/** Cap de joueurs par salle (docs/03 §2.5). Au-delà → fermeture `room_full`. */
export const MAX_ROOM_SOCKETS = 8;

/** Taille maximale d'un message réseau sérialisé (docs/03 §6.1) — au-delà : rejet. */
export const MAX_MESSAGE_BYTES = 64 * 1024;

/**
 * Génère un code de salle. `rand` par défaut = `Math.random` (suffisant pour le transport
 * BroadcastChannel de test) ; le serveur injecte une source cryptographique (`node:crypto`)
 * pour des codes non devinables (docs/03 §6.3).
 */
export function genRoomCode(rand: () => number = Math.random): string {
  let s = '';
  for (let i = 0; i < ROOM_CODE_LEN; i++) {
    s += ROOM_ALPHABET[Math.floor(rand() * ROOM_ALPHABET.length)];
  }
  return s;
}

/** Vrai si `code` a la bonne longueur et n'utilise que l'alphabet sans ambigus. */
export function isValidRoomCode(code: string): boolean {
  if (code.length !== ROOM_CODE_LEN) return false;
  for (const ch of code) {
    if (!ROOM_ALPHABET.includes(ch)) return false;
  }
  return true;
}
